/*
 | Local, display-only count of how many guesses remain against the CURRENT code, so the
 | UI can say "2 attempts left" instead of a bare "that code isn't right".
 |
 | WHY THIS IS COUNTED CLIENT-SIDE: Descope never reports the remaining count. Per Descope
 | support (CLUB-786) a wrong guess and an exhausted code are distinguishable only by the
 | error code itself (DESCOPE_ERROR_INVALID_OTP vs DESCOPE_ERROR_MAX_OTP_ATTEMPTS) — there
 | is no "attempts remaining" field to read. So the count has to be kept here.
 |
 | THIS ENFORCES NOTHING. Descope invalidates the code on its own limit regardless of what
 | this says, and the UI branches on DESCOPE_ERROR_MAX_OTP_ATTEMPTS — not on attemptsLeft —
 | to decide someone is locked out. Clearing storage or refreshing therefore buys a user
 | nothing but an inaccurate number on their own screen. (Contrast the cross-code hourly cap
 | discussed in CLUB-786, which cannot live here: there a client-side counter WOULD be the
 | only barrier, and so would be worthless.)
 |
 | Because the budget belongs to one code, reset() is called every time a code is sent or
 | resent — a fresh code carries a fresh budget.
 */

/*
 | Mirrors Descope console → OTP Settings → "Verification Attempts". Descope owns the real
 | limit; this constant only decides what number the user is shown, so the two drifting
 | apart means the copy lies (says "1 attempt left" on a code Descope has already killed, or
 | vice versa). Change both together.
 */
export const MAX_OTP_ATTEMPTS = 3

/*
 | Shown once the budget is spent, whether that was reported by Descope
 | (DESCOPE_ERROR_MAX_OTP_ATTEMPTS) or decided locally. Deliberately identical in both
 | cases: to the user the code is dead either way, and the action is the same. Lives here so
 | the three MFA surfaces, which each used their own copy of this string, cannot drift.
 */
export const EXHAUSTED_MESSAGE = 'You\'ve used all your attempts for this code. Please request a new one.'

/*
 | Copy for the full-panel state shown once the budget is spent (V2MoleculesOtpSendBlocked
 | with these props). Distinct from that molecule's own defaults, which describe the
 | SEND-side lockout — a Twilio/Descope rate limit on how many codes we may send to a number.
 | Same panel, genuinely different cause: there we paused sending, here the user got the
 | code wrong too many times and we cancelled it. Conflating the two tells a user we
 | throttled them when in fact they mistyped.
 */
/*
 | The design's shield illustration. Deliberately NOT applied to the send-side lockout,
 | which keeps its flat icon — the two panels are different states and the illustration is
 | specified for this one only.
 */
export const EXHAUSTED_PANEL_IMAGE = '/images/auth/otp-attempts-blocked.png'

export const EXHAUSTED_PANEL_TITLE = 'Too many incorrect codes'
export const EXHAUSTED_PANEL_DESCRIPTION = 'We\'ve cancelled that code for your security. You can request a new one in a moment.'

/*
 | How long before a new code may be requested after burning the budget.
 |
 | This is the FIRST RUNG ONLY of the escalation Jessica specified (60s → 5min → 15min,
 | ceiling at 10 attempts/hour). The later rungs need state that survives a refresh, and the
 | hourly ceiling needs server-side state we do not have today — see CLUB-786. Until then a
 | flat 60s is the honest subset: it matches the design's "00:58" and the first lockout, and
 | escalation slots in by making this value a function of the lockout count.
 */
export const ATTEMPT_LOCKOUT_SECONDS = 60

export default function useOtpAttempts (maxAttempts = MAX_OTP_ATTEMPTS) {
  const used = ref(0)
  // Floored at 0: the guess that exhausts the budget comes back as
  // DESCOPE_ERROR_MAX_OTP_ATTEMPTS and is handled by its own message, so "0 attempts left"
  // should never be rendered — but it must not be able to go negative if one ever is.
  const attemptsLeft = computed(() => Math.max(maxAttempts - used.value, 0))

  /*
   * The budget for this code is spent, so the UI must stop accepting guesses against it.
   *
   * WHY THE UI HAS TO GATE THIS AT ALL: observed in our tenant, Descope keeps returning
   * DESCOPE_ERROR_INVALID_OTP indefinitely rather than switching to
   * DESCOPE_ERROR_MAX_OTP_ATTEMPTS — i.e. its own "Verification Attempts" limit is not
   * currently doing anything, and a user can guess forever. Gating here is a UX correction,
   * NOT a security control (a refresh clears it); the real fix is the Descope OTP Settings
   * limit, and this gate then simply agrees with it.
   */
  const isExhausted = computed(() => attemptsLeft.value <= 0)

  // The full user-facing string for an ordinary wrong guess, kept here rather than at each
  // call site so the three MFA surfaces cannot drift apart in wording.
  const wrongCodeMessage = computed(() => {
    if (isExhausted.value) return EXHAUSTED_MESSAGE
    const attempt = attemptsLeft.value === 1 ? 'attempt' : 'attempts'
    return `That code isn't right. ${attemptsLeft.value} ${attempt} left.`
  })

  function recordFailure () {
    used.value += 1
  }

  function reset () {
    used.value = 0
  }

  return { attemptsLeft, isExhausted, wrongCodeMessage, recordFailure, reset }
}
